import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { getEnv } from '@/utils/helpers'

export interface TutorialAssets {
  video_silent: string
  video_fr: string
  subtitles_vtt_fr: string
  subtitles_srt_fr: string
  narration_txt_fr: string
  description_md: string
  video_fr_slow?: string
  // Per-language dubbed/translated assets, present only when generated.
  // Indexed access (`assets[`video_${lang}`]`) is the canonical lookup.
  [key: `video_${string}`]: string | undefined
  [key: `subtitles_vtt_${string}`]: string | undefined
  [key: `subtitles_srt_${string}`]: string | undefined
  [key: `narration_txt_${string}`]: string | undefined
}

export interface Tutorial {
  id: string
  order: number
  title: string
  // Per-language titles. `title` (above) is the FR baseline kept for
  // back-compat; lang-aware UIs should prefer titles[lang] when available.
  titles?: Partial<Record<string, string>>
  duration_s: number
  audio_duration_s: number | null
  has_stretched_variant: boolean
  assets: TutorialAssets
}

export interface TutorialManifest {
  bucket: string
  region: string
  language: string
  available_languages?: string[]
  generated_by: string
  tutorials: Tutorial[]
}

const baseUrl = getEnv('VITE_TUTORIAL_MANIFEST_URL_BASE')

export const tutorialsApi = createApi({
  reducerPath: 'tutorialsApi',
  // Keep anonymous (S3 bucket serves the manifest/tutorials prefix publicly).
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    getManifest: builder.query<TutorialManifest, void>({
      query: () => ({ url: '/manifest.json' }),
    }),
    // Fetch an arbitrary text asset from the bucket (e.g. description.md).
    // URL should be an absolute https:// URL from the manifest.
    getAssetText: builder.query<string, string>({
      queryFn: async (url) => {
        try {
          const r = await fetch(url)
          if (!r.ok) return { error: { status: r.status, data: r.statusText } as any }
          const text = await r.text()
          return { data: text }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR', error: (e as Error).message } as any }
        }
      },
    }),
  }),
})

export const { useGetManifestQuery, useGetAssetTextQuery } = tutorialsApi
